import { create } from 'zustand';
import {
  createMeshFromCorners,
  createMaskShape,
  isIdentityMesh,
  isMeshTopologyValid,
  resampleMesh,
  type CornerIndex,
  type Corners,
  type MaskShapeKind,
  type MaskVertex,
  type Point,
  type ProceduralNameV2,
  type Surface,
  type SurfaceMask,
  type SurfaceContent,
  type SurfaceSourceRegion,
  type SurfaceMediaTransform,
  type WarpPresetV2,
} from '@cornerstone/shared';
import { apiV2, type NewPresetV2Input, type WarpPresetV2ExportFile } from '../client/apiV2';
import { toast, describeApiError } from '../components/toast';

// =============================================================================
// Calibration v2 store — PLAN P3 (docs/old/CALIBRATION_V2_POLYGON_PLAN.md §3/§5).
//
// v1(store/calibration.ts) 대비:
//   - split/seam/slice/bezierPatch/WallSide 파생 로직 전부 삭제.
//   - 데이터 모델은 Surface[] (MVP는 activeSurfaceId 하나만 편집).
//   - undo/redo history에 mask·mesh 편집 포함 (v1의 mask-누락 버그 재발 금지 —
//     history 스냅샷이 surfaces 배열 전체를 담는다).
// =============================================================================

export type ConnectionStatusV2 = 'connecting' | 'online' | 'offline';

export type EditorModeV2 = 'corner' | 'mesh' | 'mask';

export type EdgeId = 'top' | 'right' | 'bottom' | 'left';

/** 변 → 양 끝 코너 인덱스 (TL·TR·BR·BL 순서 기준). */
export const EDGE_CORNERS: Record<EdgeId, [CornerIndex, CornerIndex]> = {
  top: [0, 1],
  right: [1, 2],
  bottom: [2, 3],
  left: [3, 0],
};

export type SelectionV2 =
  | { kind: 'corner'; index: CornerIndex }
  | { kind: 'mesh'; indices: number[] }
  | { kind: 'maskVertex'; indices: number[] }
  | null;

/** 캔버스 소스 — 테스트 패턴 또는 미디어 라이브러리 아이템 프리뷰 (§4.3). */
export type PreviewSourceV2 =
  | { kind: 'pattern' }
  | { kind: 'media'; mediaId: string };

export type NudgeStepV2 = 0.1 | 1 | 10;

const MAX_HISTORY = 20;

const DEFAULT_SURFACE_ID = 'surface-1';
const SURFACE_COLORS = ['#38BDF8', '#F97316', '#22C55E', '#A78BFA', '#F43F5E', '#EAB308'];

export function createDefaultSurface(): Surface {
  return {
    id: DEFAULT_SURFACE_ID,
    color: SURFACE_COLORS[0]!,
    sourceRegion: { x: 0, y: 0, width: 1, height: 1 },
    mediaTransform: { scale: 1, offsetX: 0, offsetY: 0 },
    corners: [
      { x: 0, y: 0 },
      { x: 1, y: 0 },
      { x: 1, y: 1 },
      { x: 0, y: 1 },
    ],
    fineWarpPoints: [],
    mesh: null,
    mask: null,
    content: { type: 'schedule' },
  };
}

// -----------------------------------------------------------------------------
// History & dirty hash — mask/mesh 포함 (v1 버그 해소 지점)
// -----------------------------------------------------------------------------

type HistoryEntryV2 = {
  surfaces: Surface[];
};

function snapshot(s: { surfaces: Surface[] }): HistoryEntryV2 {
  return { surfaces: s.surfaces };
}

/** 저장 대비 dirty 판정 해시 — surfaces(마스크·메시 포함) + bypassWarp. */
function stateHash(s: { surfaces: Surface[]; bypassWarp: boolean }): string {
  return JSON.stringify({ S: s.surfaces, B: s.bypassWarp });
}

// -----------------------------------------------------------------------------
// Store shape
// -----------------------------------------------------------------------------

export interface CalibrationV2State {
  /** 편집 대상 디바이스 — 캘리브레이션은 기기별 완전 격리. Calibration
   *  페이지 mount 시 설정되며, null 이면 서버 CRUD/live sync 를 막는다. */
  deviceId: string | null;
  sessionVersion: number;
  saving: boolean;
  // -------- Geometry / shape --------
  surfaces: Surface[];
  /** Source Map과 출력 캔버스에서 편집 중인 surface. */
  activeSurfaceId: string;
  bypassWarp: boolean;

  // -------- Editor session --------
  editorMode: EditorModeV2;
  selection: SelectionV2;
  nudgeStep: NudgeStepV2;
  isDragging: boolean;
  /** 메시 밀도 하향(데이터 손실) 대기 — 확인 모달 게이트. */
  pendingMeshDensity: number | null;

  // -------- Preview source --------
  testPattern: ProceduralNameV2;
  heatmapEnabled: boolean;
  previewSource: PreviewSourceV2;

  // -------- Save state --------
  activePresetId: string | null;
  lastSavedHash: string;
  lastSavedSurfaces: Surface[];
  lastSavedBypassWarp: boolean;
  liveWarpSyncReady: boolean;

  // -------- Presets (server-backed) --------
  presets: WarpPresetV2[];
  presetError: string | null;
  connectionStatus: ConnectionStatusV2;

  // -------- Undo / redo --------
  history: HistoryEntryV2[];
  historyCursor: number;

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  setEditorMode: (m: EditorModeV2) => void;

  selectCorner: (index: CornerIndex) => void;
  selectMeshPoints: (indices: number[]) => void;
  toggleMeshPoint: (index: number) => void;
  selectMaskVertices: (indices: number[]) => void;
  toggleMaskVertex: (index: number) => void;
  clearSelection: () => void;

  // Corner-pin
  moveCorner: (index: CornerIndex, point: Point) => void;
  /** 변 드래그 — 변의 두 코너를 같은 delta로 평행 이동. */
  translateEdge: (edge: EdgeId, dx: number, dy: number) => void;

  // Mesh
  /**
   * 밀도 변경 요청. 하향(다운스케일)이고 기존 메시에 변위가 있으면 즉시 적용
   * 대신 pendingMeshDensity를 세팅해 확인 모달을 요구한다.
   */
  requestMeshDensity: (n: number) => void;
  confirmMeshDensity: () => void;
  cancelMeshDensity: () => void;
  moveMeshPoint: (index: number, point: Point) => void;
  translateMeshPoints: (indices: number[], dx: number, dy: number) => void;
  /** 그리드 초기화 — 현재 밀도의 identity로 복귀 (확인 모달은 UI 몫). */
  resetMesh: () => void;

  // Mask
  applyMaskShape: (kind: MaskShapeKind) => void;
  clearMask: () => void;
  /** 엣지 edgeIndex(→edgeIndex+1 사이)에 정점 삽입. 삽입 정점 선택. */
  addMaskVertex: (edgeIndex: number, point: Point) => void;
  /** 3점 미만이 되는 삭제는 무시. */
  deleteMaskVertices: (indices: number[]) => void;
  moveMaskVertex: (index: number, point: Point) => void;
  translateMaskVertices: (indices: number[], dx: number, dy: number) => void;
  /** 현재 bounding box 중심을 유지하며 마스크 전체 크기를 변경한다. */
  resizeMask: (width: number, height: number) => void;
  /** 마스크 전체 중심을 이동한다. 캔버스 범위를 벗어나지 않게 보정한다. */
  moveMask: (centerX: number, centerY: number) => void;
  moveMaskHandle: (index: number, which: 'h1' | 'h2', point: Point) => void;
  /** 직선 ↔ 곡선(베지어) 전환 — 곡선화 시 h1/h2 자동 생성. */
  toggleMaskVertexCurve: (index: number) => void;
  setMaskFeather: (px: number) => void;
  setMaskInvert: (invert: boolean) => void;

  // Surface content (§10)
  setSurfaceContent: (content: SurfaceContent) => void;
  setActiveSurface: (id: string) => void;
  addSurface: () => void;
  removeActiveSurface: () => void;
  applySurfaceGrid: (cols: number, rows: number) => void;
  setSurfaceColor: (color: string) => void;
  setSourceRegion: (region: SurfaceSourceRegion) => void;
  setSurfaceMediaTransform: (transform: SurfaceMediaTransform) => void;
  setBypassWarp: (b: boolean) => void;

  // Nudge
  nudgeSelected: (dx: number, dy: number) => void;
  setNudgeStep: (n: NudgeStepV2) => void;

  // Preview source
  setTestPattern: (p: ProceduralNameV2) => void;
  setHeatmapEnabled: (b: boolean) => void;
  setPreviewSource: (src: PreviewSourceV2) => void;

  // Drag lifecycle — history는 드래그 종료 시 한 번만 기록
  beginDrag: () => void;
  endDrag: () => void;

  // Persistence
  loadPreset: (p: WarpPresetV2) => void;
  hydrateLive: (input: {
    activePresetId: string | null;
    savedPreset?: WarpPresetV2;
    surfaces: Surface[];
    bypassWarp: boolean;
  }) => void;
  resetToDefault: () => void;
  revertToSaved: () => void;
  setLiveWarpSyncReady: (ready: boolean) => void;
  setConnectionStatus: (s: ConnectionStatusV2) => void;

  // Server-backed preset CRUD (v2 API)
  setDeviceId: (deviceId: string | null) => void;
  loadPresets: () => Promise<WarpPresetV2[]>;
  saveOverwrite: () => Promise<WarpPresetV2 | null>;
  saveAs: (name: string) => Promise<WarpPresetV2 | null>;
  applyPresetById: (id: string) => Promise<void>;
  duplicatePresetById: (id: string) => Promise<void>;
  deletePresetById: (id: string) => Promise<void>;
  togglePresetProtected: (id: string) => Promise<void>;
  exportPresets: () => Promise<void>;
  importPresets: (file: File) => Promise<number>;
  receivePresetSaved: (p: WarpPresetV2) => void;
  receivePresetDeleted: (id: string) => void;
  clearPresetError: () => void;

  // Undo / Redo
  undo: () => void;
  redo: () => void;
}

// -----------------------------------------------------------------------------
// Pure helpers
// -----------------------------------------------------------------------------

function replaceCorner(corners: Corners, index: CornerIndex, point: Point): Corners {
  const [a, b, c, d] = corners;
  const arr: [Point, Point, Point, Point] = [a, b, c, d];
  arr[index] = point;
  return arr;
}

function mapCorners(corners: Corners, fn: (corner: Point) => Point): Corners {
  return [fn(corners[0]), fn(corners[1]), fn(corners[2]), fn(corners[3])];
}

/** activeSurface에 patch 함수를 적용한 surfaces 배열을 만든다. */
function patchActiveSurface(
  s: Pick<CalibrationV2State, 'surfaces' | 'activeSurfaceId'>,
  fn: (surface: Surface) => Surface,
): Surface[] {
  return s.surfaces.map((sf) => (sf.id === s.activeSurfaceId ? fn(sf) : sf));
}

export function activeSurfaceOf(
  s: Pick<CalibrationV2State, 'surfaces' | 'activeSurfaceId'>,
): Surface | null {
  return s.surfaces.find((sf) => sf.id === s.activeSurfaceId) ?? null;
}

/** 정점 이동 — p와 함께 h1/h2도 같은 delta로 따라간다 (핸들 상대 위치 보존). */
function translateVertex(v: MaskVertex, dx: number, dy: number): MaskVertex {
  const out: MaskVertex = { p: { x: v.p.x + dx, y: v.p.y + dy } };
  if (v.h1) out.h1 = { x: v.h1.x + dx, y: v.h1.y + dy };
  if (v.h2) out.h2 = { x: v.h2.x + dx, y: v.h2.y + dy };
  return out;
}

/** 도형 편집 시 프리셋 kind → 'custom' 승격 (PLAN §5.2). */
function promoteToCustom(mask: SurfaceMask): SurfaceMask {
  return mask.kind === 'custom' ? mask : { ...mask, kind: 'custom' };
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
const samePoint = (a: Point, b: Point): boolean =>
  Math.abs(a.x - b.x) < 1e-8 && Math.abs(a.y - b.y) < 1e-8;

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

const INITIAL_SURFACES = [createDefaultSurface()];
const INITIAL_HASH = stateHash({ surfaces: INITIAL_SURFACES, bypassWarp: false });

export const useCalibrationV2Store = create<CalibrationV2State>((set, get) => ({
  surfaces: INITIAL_SURFACES,
  activeSurfaceId: DEFAULT_SURFACE_ID,
  bypassWarp: false,

  editorMode: 'corner',
  selection: null,
  nudgeStep: 1,
  isDragging: false,
  pendingMeshDensity: null,

  testPattern: 'checker',
  heatmapEnabled: false,
  previewSource: { kind: 'pattern' },

  activePresetId: null,
  lastSavedHash: INITIAL_HASH,
  lastSavedSurfaces: INITIAL_SURFACES,
  lastSavedBypassWarp: false,
  liveWarpSyncReady: false,
  deviceId: null,
  sessionVersion: 0,
  saving: false,

  presets: [],
  presetError: null,
  connectionStatus: 'connecting',

  history: [snapshot({ surfaces: INITIAL_SURFACES })],
  historyCursor: 0,

  // -------- Editor session --------
  setEditorMode: (editorMode) => set({ editorMode, selection: null }),

  selectCorner: (index) => set({ selection: { kind: 'corner', index } }),
  selectMeshPoints: (indices) =>
    set({ selection: indices.length ? { kind: 'mesh', indices } : null }),
  toggleMeshPoint: (index) =>
    set((s) => {
      const cur = s.selection?.kind === 'mesh' ? s.selection.indices : [];
      const next = cur.includes(index)
        ? cur.filter((i) => i !== index)
        : [...cur, index];
      return { selection: next.length ? { kind: 'mesh', indices: next } : null };
    }),
  selectMaskVertices: (indices) =>
    set({ selection: indices.length ? { kind: 'maskVertex', indices } : null }),
  toggleMaskVertex: (index) =>
    set((s) => {
      const cur = s.selection?.kind === 'maskVertex' ? s.selection.indices : [];
      const next = cur.includes(index)
        ? cur.filter((i) => i !== index)
        : [...cur, index];
      return { selection: next.length ? { kind: 'maskVertex', indices: next } : null };
    }),
  clearSelection: () => set({ selection: null }),

  // -------- Corner-pin --------
  moveCorner: (index, point) =>
    set((s) => {
      const active = activeSurfaceOf(s);
      if (!active) return {};
      const origin = active.corners[index];
      return commit(s, {
        surfaces: s.surfaces.map((sf) => ({
          ...sf,
          corners: mapCorners(sf.corners, (corner) =>
            samePoint(corner, origin) ? point : corner,
          ),
        })),
        selection: { kind: 'corner', index },
      });
    }),

  translateEdge: (edge, dx, dy) =>
    set((s) => {
      const active = activeSurfaceOf(s);
      if (!active) return {};
      const [a, b] = EDGE_CORNERS[edge];
      const origins = [active.corners[a], active.corners[b]];
      return commit(s, {
        surfaces: s.surfaces.map((sf) => ({
          ...sf,
          corners: mapCorners(sf.corners, (corner) =>
            origins.some((origin) => samePoint(corner, origin))
              ? { x: corner.x + dx, y: corner.y + dy }
              : corner,
          ),
        })),
      });
    }),

  // -------- Mesh --------
  requestMeshDensity: (n) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      if (!sf) return {};
      const mesh = sf.mesh;
      if (mesh && !isIdentityMesh(mesh) && n < mesh.cols) {
        // 다운스케일 — 셀 내부 변위가 소실될 수 있으므로 확인 게이트.
        return { pendingMeshDensity: n };
      }
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mesh: surface.mesh
            ? resampleMesh(surface.mesh, n, n)
            : createMeshFromCorners(surface.corners, n, n),
        })),
        selection: null,
        pendingMeshDensity: null,
      });
    }),

  confirmMeshDensity: () =>
    set((s) => {
      const n = s.pendingMeshDensity;
      if (n === null) return {};
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mesh: surface.mesh
            ? resampleMesh(surface.mesh, n, n)
            : createMeshFromCorners(surface.corners, n, n),
        })),
        selection: null,
        pendingMeshDensity: null,
      });
    }),

  cancelMeshDensity: () => set({ pendingMeshDensity: null }),

  moveMeshPoint: (index, point) =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => {
          if (!sf.mesh || index < 0 || index >= sf.mesh.points.length) return sf;
          const points = sf.mesh.points.map((p, i) => (i === index ? point : p));
          const mesh = { ...sf.mesh, points };
          return isMeshTopologyValid(mesh) ? { ...sf, mesh } : sf;
        }),
      }),
    ),

  translateMeshPoints: (indices, dx, dy) =>
    set((s) => {
      if (indices.length === 0) return {};
      const idxSet = new Set(indices);
      return commit(s, {
        surfaces: patchActiveSurface(s, (sf) => {
          if (!sf.mesh) return sf;
          const points = sf.mesh.points.map((p, i) =>
            idxSet.has(i) ? { x: p.x + dx, y: p.y + dy } : p,
          );
          const mesh = { ...sf.mesh, points };
          return isMeshTopologyValid(mesh) ? { ...sf, mesh } : sf;
        }),
      });
    }),

  resetMesh: () =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({
          ...sf,
          mesh: sf.mesh
            ? createMeshFromCorners(sf.corners, sf.mesh.cols, sf.mesh.rows)
            : null,
        })),
        selection: null,
      }),
    ),

  // -------- Mask --------
  applyMaskShape: (kind) =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({
          ...sf,
          mask: createMaskShape(kind),
        })),
        selection: null,
      }),
    ),

  clearMask: () =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({ ...sf, mask: null })),
        selection: null,
      }),
    ),

  addMaskVertex: (edgeIndex, point) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      if (!mask) return {};
      const n = mask.vertices.length;
      const at = ((edgeIndex % n) + n) % n;
      const vertices = [
        ...mask.vertices.slice(0, at + 1),
        { p: { x: clamp01(point.x), y: clamp01(point.y) } },
        ...mask.vertices.slice(at + 1),
      ];
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
        selection: { kind: 'maskVertex', indices: [at + 1] },
      });
    }),

  deleteMaskVertices: (indices) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      if (!mask || indices.length === 0) return {};
      const idxSet = new Set(indices);
      const vertices = mask.vertices.filter((_, i) => !idxSet.has(i));
      // 폴리곤 최소 3점 유지 — 그 아래로 내려가는 삭제는 무시.
      if (vertices.length < 3) return {};
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
        selection: null,
      });
    }),

  moveMaskVertex: (index, point) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      const v = mask?.vertices[index];
      if (!mask || !v) return {};
      const dx = point.x - v.p.x;
      const dy = point.y - v.p.y;
      const vertices = mask.vertices.map((vv, i) =>
        i === index ? translateVertex(vv, dx, dy) : vv,
      );
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
      });
    }),

  translateMaskVertices: (indices, dx, dy) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      if (!mask || indices.length === 0) return {};
      const idxSet = new Set(indices);
      const vertices = mask.vertices.map((v, i) =>
        idxSet.has(i) ? translateVertex(v, dx, dy) : v,
      );
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
      });
    }),

  resizeMask: (width, height) =>
    set((s) => {
      const mask = activeSurfaceOf(s)?.mask;
      if (!mask || mask.vertices.length === 0) return {};
      const xs = mask.vertices.map((v) => v.p.x);
      const ys = mask.vertices.map((v) => v.p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const currentWidth = maxX - minX;
      const currentHeight = maxY - minY;
      if (currentWidth <= 1e-6 || currentHeight <= 1e-6) return {};
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const requestedScaleX = Math.max(0.01, width) / currentWidth;
      const requestedScaleY = Math.max(0.01, height) / currentHeight;
      const maxScaleX = mask.vertices.reduce((limit, v) => {
        const dx = v.p.x - cx;
        if (dx > 1e-6) return Math.min(limit, (1 - cx) / dx);
        if (dx < -1e-6) return Math.min(limit, cx / -dx);
        return limit;
      }, Number.POSITIVE_INFINITY);
      const maxScaleY = mask.vertices.reduce((limit, v) => {
        const dy = v.p.y - cy;
        if (dy > 1e-6) return Math.min(limit, (1 - cy) / dy);
        if (dy < -1e-6) return Math.min(limit, cy / -dy);
        return limit;
      }, Number.POSITIVE_INFINITY);
      let scaleX = Math.min(requestedScaleX, maxScaleX);
      let scaleY = Math.min(requestedScaleY, maxScaleY);
      // UI의 비율 고정 조작은 두 축에 같은 배율을 요청한다. 한 축이 먼저
      // 캔버스 경계에 닿아도 종횡비가 깨지지 않도록 공통 허용 배율을 쓴다.
      if (Math.abs(requestedScaleX - requestedScaleY) <= 1e-6) {
        const uniformScale = Math.min(requestedScaleX, maxScaleX, maxScaleY);
        scaleX = uniformScale;
        scaleY = uniformScale;
      }
      const scalePoint = (p: Point): Point => ({
        x: cx + (p.x - cx) * scaleX,
        y: cy + (p.y - cy) * scaleY,
      });
      const vertices = mask.vertices.map((v): MaskVertex => ({
        p: scalePoint(v.p),
        ...(v.h1 ? { h1: scalePoint(v.h1) } : {}),
        ...(v.h2 ? { h2: scalePoint(v.h2) } : {}),
      }));
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: surface.mask ? { ...surface.mask, vertices } : null,
        })),
      });
    }),

  moveMask: (centerX, centerY) =>
    set((s) => {
      const mask = activeSurfaceOf(s)?.mask;
      if (!mask || mask.vertices.length === 0) return {};
      const xs = mask.vertices.map((v) => v.p.x);
      const ys = mask.vertices.map((v) => v.p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const currentX = (minX + maxX) / 2;
      const currentY = (minY + maxY) / 2;
      const dx = centerX - currentX;
      const dy = centerY - currentY;
      const vertices = mask.vertices.map((v) => translateVertex(v, dx, dy));
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: surface.mask ? { ...surface.mask, vertices } : null,
        })),
      });
    }),

  moveMaskHandle: (index, which, point) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      const v = mask?.vertices[index];
      if (!mask || !v) return {};
      const vertices = mask.vertices.map((vv, i) =>
        i === index ? { ...vv, [which]: point } : vv,
      );
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
      });
    }),

  toggleMaskVertexCurve: (index) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      const mask = sf?.mask;
      const v = mask?.vertices[index];
      if (!mask || !v) return {};
      const n = mask.vertices.length;
      let next: MaskVertex;
      if (v.h1 || v.h2) {
        // 곡선 → 직선: 핸들 제거.
        next = { p: v.p };
      } else {
        // 직선 → 곡선: 이웃 방향으로 1/4 지점에 핸들 생성.
        const prev = mask.vertices[(index - 1 + n) % n]!;
        const after = mask.vertices[(index + 1) % n]!;
        next = {
          p: v.p,
          h1: {
            x: v.p.x + (prev.p.x - v.p.x) * 0.25,
            y: v.p.y + (prev.p.y - v.p.y) * 0.25,
          },
          h2: {
            x: v.p.x + (after.p.x - v.p.x) * 0.25,
            y: v.p.y + (after.p.y - v.p.y) * 0.25,
          },
        };
      }
      const vertices = mask.vertices.map((vv, i) => (i === index ? next : vv));
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: promoteToCustom({ ...mask, vertices }),
        })),
        selection: { kind: 'maskVertex', indices: [index] },
      });
    }),

  setMaskFeather: (px) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      if (!sf?.mask) return {};
      // feather/invert는 모양 편집이 아니므로 kind 승격 없음.
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: surface.mask ? { ...surface.mask, feather: Math.max(0, px) } : null,
        })),
      });
    }),

  setMaskInvert: (invert) =>
    set((s) => {
      const sf = activeSurfaceOf(s);
      if (!sf?.mask) return {};
      return commit(s, {
        surfaces: patchActiveSurface(s, (surface) => ({
          ...surface,
          mask: surface.mask ? { ...surface.mask, invert } : null,
        })),
      });
    }),

  // -------- Surface content / bypass --------
  setSurfaceContent: (content) =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({ ...sf, content })),
      }),
    ),

  setActiveSurface: (activeSurfaceId) =>
    set((s) =>
      s.surfaces.some((surface) => surface.id === activeSurfaceId)
        ? { activeSurfaceId, selection: null }
        : {},
    ),

  addSurface: () =>
    set((s) => {
      const id = `surface-${Date.now().toString(36)}`;
      const next: Surface = {
        ...createDefaultSurface(),
        id,
        color: SURFACE_COLORS[s.surfaces.length % SURFACE_COLORS.length]!,
      };
      return {
        ...commit(s, { surfaces: [...s.surfaces, next] }),
        activeSurfaceId: id,
        selection: null,
      };
    }),

  removeActiveSurface: () =>
    set((s) => {
      if (s.surfaces.length <= 1) return {};
      const surfaces = s.surfaces.filter((sf) => sf.id !== s.activeSurfaceId);
      return {
        ...commit(s, { surfaces }),
        activeSurfaceId: surfaces[0]!.id,
        selection: null,
      };
    }),

  applySurfaceGrid: (cols, rows) =>
    set((s) => {
      const safeCols = Math.max(1, Math.min(4, Math.floor(cols)));
      const safeRows = Math.max(1, Math.min(4, Math.floor(rows)));
      const surfaces: Surface[] = [];
      for (let row = 0; row < safeRows; row++) {
        for (let col = 0; col < safeCols; col++) {
          const x = col / safeCols;
          const y = row / safeRows;
          const width = 1 / safeCols;
          const height = 1 / safeRows;
          const index = row * safeCols + col;
          surfaces.push({
            ...createDefaultSurface(),
            id: `surface-${index + 1}`,
            color: SURFACE_COLORS[index % SURFACE_COLORS.length]!,
            sourceRegion: { x, y, width, height },
            corners: [
              { x, y },
              { x: x + width, y },
              { x: x + width, y: y + height },
              { x, y: y + height },
            ],
          });
        }
      }
      return {
        ...commit(s, { surfaces }),
        activeSurfaceId: surfaces[0]!.id,
        selection: null,
      };
    }),

  setSurfaceColor: (color) =>
    set((s) =>
      commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({ ...sf, color })),
      }),
    ),

  setSourceRegion: (region) =>
    set((s) => {
      const width = Math.max(0.01, Math.min(1, region.width));
      const height = Math.max(0.01, Math.min(1, region.height));
      const normalized = {
        x: Math.max(0, Math.min(1 - width, region.x)),
        y: Math.max(0, Math.min(1 - height, region.y)),
        width,
        height,
      };
      const corners: Corners = [
        { x: normalized.x, y: normalized.y },
        { x: normalized.x + normalized.width, y: normalized.y },
        {
          x: normalized.x + normalized.width,
          y: normalized.y + normalized.height,
        },
        { x: normalized.x, y: normalized.y + normalized.height },
      ];
      return commit(s, {
        surfaces: patchActiveSurface(s, (sf) => ({
          ...sf,
          sourceRegion: normalized,
          corners,
          mesh: sf.mesh ? createMeshFromCorners(corners, sf.mesh.cols, sf.mesh.rows) : null,
        })),
      });
    }),

  setSurfaceMediaTransform: (transform) =>
    set((s) => {
      const normalized = {
        scale: Math.max(1, Math.min(3, transform.scale)),
        offsetX: Math.max(-0.5, Math.min(0.5, transform.offsetX)),
        offsetY: Math.max(-0.5, Math.min(0.5, transform.offsetY)),
      };
      const active = activeSurfaceOf(s);
      return commit(s, {
        surfaces: active?.content.type === 'schedule'
          ? s.surfaces.map((sf) => sf.content.type === 'schedule'
            ? { ...sf, mediaTransform: normalized }
            : sf)
          : patchActiveSurface(s, (sf) => ({ ...sf, mediaTransform: normalized })),
      });
    }),

  setBypassWarp: (bypassWarp) => set({ bypassWarp }),

  // -------- Nudge --------
  nudgeSelected: (dx, dy) => {
    const s = get();
    if (!s.selection) return;
    if (s.selection.kind === 'corner') {
      const sf = activeSurfaceOf(s);
      if (!sf) return;
      const cur = sf.corners[s.selection.index];
      s.moveCorner(s.selection.index, { x: cur.x + dx, y: cur.y + dy });
      return;
    }
    if (s.selection.kind === 'mesh') {
      s.translateMeshPoints(s.selection.indices, dx, dy);
      return;
    }
    s.translateMaskVertices(s.selection.indices, dx, dy);
  },

  setNudgeStep: (nudgeStep) => set({ nudgeStep }),

  // -------- Preview --------
  setTestPattern: (testPattern) => set({ testPattern }),
  setHeatmapEnabled: (heatmapEnabled) => set({ heatmapEnabled }),
  setPreviewSource: (previewSource) => set({ previewSource }),

  // -------- Drag lifecycle --------
  beginDrag: () => set({ isDragging: true }),
  endDrag: () =>
    set((s) => {
      if (!s.isDragging) return {};
      return { isDragging: false, ...pushHistoryPatch(s) };
    }),

  // -------- Persistence --------
  loadPreset: (p) =>
    set(() => {
      const surfaces = p.surfaces.length > 0 ? p.surfaces : [createDefaultSurface()];
      return {
        surfaces,
        activeSurfaceId: surfaces[0]!.id,
        bypassWarp: p.bypassWarp,
        activePresetId: p.id,
        lastSavedHash: stateHash({ surfaces, bypassWarp: p.bypassWarp }),
        lastSavedSurfaces: surfaces,
        lastSavedBypassWarp: p.bypassWarp,
        selection: null,
        pendingMeshDensity: null,
        history: [snapshot({ surfaces })],
        historyCursor: 0,
      };
    }),

  hydrateLive: (input) =>
    set(() => {
      const surfaces =
        input.surfaces.length > 0 ? input.surfaces : [createDefaultSurface()];
      return {
        surfaces,
        activeSurfaceId: surfaces[0]!.id,
        bypassWarp: input.bypassWarp,
        activePresetId: input.activePresetId,
        lastSavedHash: input.savedPreset ? stateHash(input.savedPreset) : 'unsaved-live',
        lastSavedSurfaces: input.savedPreset?.surfaces ?? surfaces,
        lastSavedBypassWarp: input.savedPreset?.bypassWarp ?? input.bypassWarp,
        selection: null,
        pendingMeshDensity: null,
        history: [snapshot({ surfaces })],
        historyCursor: 0,
      };
    }),

  resetToDefault: () =>
    set((s) =>
      commit(s, {
        surfaces: [createDefaultSurface()],
        activeSurfaceId: DEFAULT_SURFACE_ID,
        selection: null,
        pendingMeshDensity: null,
      }),
    ),

  revertToSaved: () =>
    set((s) => ({
      ...commit(s, {
        surfaces: s.lastSavedSurfaces,
        bypassWarp: s.lastSavedBypassWarp,
        selection: null,
      }),
      activeSurfaceId: s.lastSavedSurfaces[0]?.id ?? DEFAULT_SURFACE_ID,
    })),

  setDeviceId: (deviceId) => set(s => s.deviceId === deviceId ? {} : {
    deviceId, sessionVersion: s.sessionVersion + 1, saving: false, liveWarpSyncReady: false,
    surfaces: INITIAL_SURFACES, activeSurfaceId: DEFAULT_SURFACE_ID, bypassWarp: false,
    activePresetId: null, presets: [], presetError: null, selection: null, pendingMeshDensity: null,
    lastSavedHash: INITIAL_HASH, lastSavedSurfaces: INITIAL_SURFACES, lastSavedBypassWarp: false,
    history: [snapshot({ surfaces: INITIAL_SURFACES })], historyCursor: 0,
  }),
  setLiveWarpSyncReady: (liveWarpSyncReady) => set({ liveWarpSyncReady }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  // -------- Server-backed preset CRUD --------
  loadPresets: async () => {
    const { deviceId, sessionVersion } = get();
    if (!deviceId) return [];
    try {
      const presets = await apiV2.listPresets(deviceId);
      if (get().sessionVersion !== sessionVersion) return [];
      set({ presets, presetError: null });
      return presets;
    } catch (e) {
      if (get().sessionVersion === sessionVersion) set({ presetError: describeApiError(e) });
      return [];
    }
  },

  saveOverwrite: async () => {
    const s = get();
    if (!s.activePresetId || !s.deviceId || s.saving) return null;
    set({ saving: true });
    try {
      const updated = await apiV2.updatePreset(s.activePresetId, { surfaces: s.surfaces, bypassWarp: s.bypassWarp }, true);
      if (get().sessionVersion !== s.sessionVersion) return null;
      set(st => ({
        lastSavedHash: stateHash(updated), lastSavedSurfaces: updated.surfaces, lastSavedBypassWarp: updated.bypassWarp,
        presets: st.presets.map(p => p.id === updated.id ? updated : p), presetError: null,
      }));
      return updated;
    } catch (e) {
      if (get().sessionVersion === s.sessionVersion) {
        toast(describeApiError(e), 'error'); set({ presetError: describeApiError(e) });
      }
      return null;
    } finally { if (get().sessionVersion === s.sessionVersion) set({ saving: false }); }
  },

  saveAs: async (name) => {
    const s = get();
    if (!s.deviceId || s.saving) return null;
    set({ saving: true });
    const input: NewPresetV2Input = {
      deviceId: s.deviceId, name, surfaces: s.surfaces, bypassWarp: s.bypassWarp,
      outputResolutionW: 3840, outputResolutionH: 2160, protected: false,
    };
    try {
      const created = await apiV2.createPreset(input, true);
      if (get().sessionVersion !== s.sessionVersion) return null;
      set(st => ({
        activePresetId: created.id, lastSavedHash: stateHash(created),
        lastSavedSurfaces: created.surfaces, lastSavedBypassWarp: created.bypassWarp,
        presets: [created, ...st.presets.filter(p => p.id !== created.id)], presetError: null,
      }));
      return created;
    } catch (e) {
      if (get().sessionVersion === s.sessionVersion) {
        toast(describeApiError(e), 'error'); set({ presetError: describeApiError(e) });
      }
      return null;
    } finally { if (get().sessionVersion === s.sessionVersion) set({ saving: false }); }
  },

  applyPresetById: async (id) => {
    const s = get();
    if (!s.deviceId || s.saving) return;
    set({ saving: true, liveWarpSyncReady: false });
    try {
      const preset = await apiV2.applyPreset(s.deviceId, id);
      if (get().sessionVersion !== s.sessionVersion) return;
      // Edits made while Apply was in flight remain dirty and are synced next.
      if (stateHash(get()) === stateHash(s)) get().loadPreset(preset);
      else set({ activePresetId: preset.id, lastSavedHash: stateHash(preset),
        lastSavedSurfaces: preset.surfaces, lastSavedBypassWarp: preset.bypassWarp });
      set({ presetError: null, liveWarpSyncReady: true });
    } catch (e) {
      if (get().sessionVersion === s.sessionVersion) {
        toast(describeApiError(e), 'error'); set({ presetError: describeApiError(e), liveWarpSyncReady: s.liveWarpSyncReady });
      }
    } finally { if (get().sessionVersion === s.sessionVersion) set({ saving: false }); }
  },

  duplicatePresetById: async (id) => {
    try {
      const copy = await apiV2.duplicatePreset(id);
      set((st) => ({
        presets: [copy, ...st.presets.filter((p) => p.id !== copy.id)],
        presetError: null,
      }));
    } catch (e) {
      toast(describeApiError(e), 'error');
      set({ presetError: describeApiError(e) });
    }
  },

  deletePresetById: async (id) => {
    try {
      await apiV2.deletePreset(id);
      set((st) => ({
        presets: st.presets.filter((p) => p.id !== id),
        activePresetId: st.activePresetId === id ? null : st.activePresetId,
        presetError: null,
      }));
    } catch (e) {
      toast(describeApiError(e), 'error');
      set({ presetError: describeApiError(e) });
    }
  },

  togglePresetProtected: async (id) => {
    const current = get().presets.find((p) => p.id === id);
    if (!current) return;
    try {
      const updated = await apiV2.setPresetProtected(id, !current.protected);
      set((st) => ({
        presets: st.presets.map((p) => (p.id === id ? updated : p)),
        presetError: null,
      }));
    } catch (e) {
      toast(describeApiError(e), 'error');
      set({ presetError: describeApiError(e) });
    }
  },

  exportPresets: async () => {
    try {
      const deviceId = get().deviceId;
      if (!deviceId) throw new Error('deviceId not set');
      const file = await apiV2.exportPresets(deviceId);
      const blob = new Blob([JSON.stringify(file, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warp-presets-v2-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      set({ presetError: null });
    } catch (e) {
      toast(describeApiError(e), 'error');
      set({ presetError: describeApiError(e) });
    }
  },

  importPresets: async (file) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch (e) {
      const msg = `가져오기 실패: ${(e as Error).message}`;
      toast(msg, 'error');
      set({ presetError: msg });
      throw new Error(msg);
    }
    const candidate = parsed as { schemaVersion?: unknown; presets?: unknown };
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      candidate.schemaVersion !== 2 ||
      !Array.isArray(candidate.presets)
    ) {
      // 구버전(v1, schemaVersion 1) 파일은 명시적으로 거부 — PLAN §6.
      const msg =
        candidate?.schemaVersion === 1
          ? '가져오기 실패: v1 preset 백업 파일입니다. Calibration v2 형식(schemaVersion 2)만 지원합니다.'
          : '가져오기 실패: warp preset v2 백업 파일이 아닙니다.';
      toast(msg, 'error');
      set({ presetError: msg });
      throw new Error(msg);
    }
    try {
      const deviceId = get().deviceId;
      if (!deviceId) throw new Error('deviceId not set');
      const { imported } = await apiV2.importPresets(
        deviceId,
        parsed as WarpPresetV2ExportFile,
      );
      set((st) => {
        const existingIds = new Set(st.presets.map((p) => p.id));
        const fresh = imported.filter((p) => !existingIds.has(p.id));
        return { presets: [...fresh, ...st.presets], presetError: null };
      });
      return imported.length;
    } catch (e) {
      const msg = describeApiError(e);
      toast(msg, 'error');
      set({ presetError: msg });
      throw new Error(msg);
    }
  },

  receivePresetSaved: (p) =>
    set((st) => p.deviceId !== st.deviceId ? {} : ({
      presets: st.presets.some((x) => x.id === p.id)
        ? st.presets.map((x) => (x.id === p.id ? p : x))
        : [p, ...st.presets],
    })),

  receivePresetDeleted: (id) =>
    set((st) => ({
      presets: st.presets.filter((p) => p.id !== id),
      activePresetId: st.activePresetId === id ? null : st.activePresetId,
    })),

  clearPresetError: () => set({ presetError: null }),

  // -------- Undo / Redo --------
  undo: () =>
    set((s) => {
      if (s.historyCursor <= 0) return {};
      const nextCursor = s.historyCursor - 1;
      const entry = s.history[nextCursor];
      if (!entry) return {};
      return { surfaces: entry.surfaces, historyCursor: nextCursor, selection: null };
    }),

  redo: () =>
    set((s) => {
      if (s.historyCursor >= s.history.length - 1) return {};
      const nextCursor = s.historyCursor + 1;
      const entry = s.history[nextCursor];
      if (!entry) return {};
      return { surfaces: entry.surfaces, historyCursor: nextCursor, selection: null };
    }),
}));

// -----------------------------------------------------------------------------
// commit / history helpers
// -----------------------------------------------------------------------------

/**
 * surfaces 변경 patch를 적용하고, 드래그 중이 아니면 history를 한 스텝 쌓는다.
 * history 스냅샷은 surfaces 전체(mask·mesh 포함) — v1의 mask 누락 버그 해소.
 */
function commit(
  s: CalibrationV2State,
  patch: Partial<CalibrationV2State> & { surfaces: Surface[] },
): Partial<CalibrationV2State> {
  if (s.isDragging) {
    // history push는 endDrag()에서 한 번만.
    return patch;
  }
  return { ...patch, ...pushHistoryPatch({ ...s, ...patch }) };
}

function pushHistoryPatch(s: {
  surfaces: Surface[];
  history: HistoryEntryV2[];
  historyCursor: number;
}): Pick<CalibrationV2State, 'history' | 'historyCursor'> {
  const entry = snapshot(s);
  const base = s.history.slice(0, s.historyCursor + 1);
  base.push(entry);
  const trimmed =
    base.length > MAX_HISTORY ? base.slice(base.length - MAX_HISTORY) : base;
  return { history: trimmed, historyCursor: trimmed.length - 1 };
}

// -----------------------------------------------------------------------------
// Derived selectors
// -----------------------------------------------------------------------------

export const useActiveSurfaceV2 = (): Surface | null =>
  useCalibrationV2Store((s) => activeSurfaceOf(s));

export const useIsDirtyV2 = (): boolean =>
  useCalibrationV2Store((s) => stateHash(s) !== s.lastSavedHash);

export const useSaveStatusV2 = (): 'saved' | 'unsaved' | 'new' =>
  useCalibrationV2Store((s) => {
    if (!s.activePresetId) return 'new';
    const dirty = stateHash(s) !== s.lastSavedHash;
    if (!dirty) return 'saved';
    return s.activePresetId !== null ? 'unsaved' : 'new';
  });

export const useCanUndoV2 = (): boolean =>
  useCalibrationV2Store((s) => s.historyCursor > 0);

export const useCanRedoV2 = (): boolean =>
  useCalibrationV2Store((s) => s.historyCursor < s.history.length - 1);
